import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
// Shell + landing load eagerly for instant first paint. Everything else is
// code-split so the first load ships only what the homepage needs.
import HomePage from "./pages/HomePage";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import InstallPWAPrompt from "./components/InstallPWAPrompt";
import CookieConsent from "./components/CookieConsent";
import ShareCriticalThinking from "./components/ShareCriticalThinking";
import ResetPasswordForm from "./components/ResetPasswordForm";
import RouteSeo from "./components/RouteSeo";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { AuthRedirect } from "./components/SignUpPrompt";
// Blogcast pages load eagerly: their code-split chunk fails to resolve behind
// the preview proxy, so we bundle them with the shell to guarantee they open.
import Blogcast from "./pages/Blogcast";
import BlogcastShow from "./pages/BlogcastShow";

const InstallApp = lazy(() => import("./pages/InstallApp"));
const BiasesHub = lazy(() => import("./pages/BiasesHub"));
const RhetoricHub = lazy(() => import("./pages/RhetoricHub"));
const PublicSpeakingHub = lazy(() => import("./pages/PublicSpeakingHub"));
const ModulesHub = lazy(() => import("./pages/ModulesHub"));
const CognitiveResourcesHub = lazy(() => import("./pages/CognitiveResourcesHub"));
const JourneyPage = lazy(() => import("./pages/JourneyPage"));
const CorporatePage = lazy(() => import("./pages/CorporatePage"));
const CorporateOffering = lazy(() => import("./pages/CorporateOffering"));
const CorporateEnglish = lazy(() => import("./pages/CorporateEnglish"));
const CorporateEnglishAon = lazy(() => import("./pages/CorporateEnglishAon"));
const AonHome = lazy(() => import("./pages/AonHome"));
const SampleCertificate = lazy(() => import("./pages/SampleCertificate"));
const BiasLibrary = lazy(() => import("./pages/BiasLibrary"));
const BiasDetail = lazy(() => import("./pages/BiasDetail"));
const BiasScanner = lazy(() => import("./pages/BiasScanner"));
const RhetoricalScanner = lazy(() => import("./pages/RhetoricalScanner"));
const RhetoricalProgressTracker = lazy(() => import("./pages/RhetoricalProgressTracker"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const Admin = lazy(() => import("./pages/Admin"));
const RhetoricDevices = lazy(() => import("./pages/RhetoricDevices"));
const RhetoricDeviceDetail = lazy(() => import("./pages/RhetoricDeviceDetail"));
const KinesicsLibrary = lazy(() => import("./pages/KinesicsLibrary"));
const KinesicsDetail = lazy(() => import("./pages/KinesicsDetail"));
const ReadTheRoom = lazy(() => import("./pages/ReadTheRoom"));
const SpeechAnalyzer = lazy(() => import("./pages/SpeechAnalyzer"));
const MyDevices = lazy(() => import("./pages/MyDevices"));
const CognitiveMoodBuilder = lazy(() => import("./pages/CognitiveMoodBuilder"));
const CriticalThinkingJournal = lazy(() => import("./pages/CriticalThinkingJournal"));
const CourseNotesJournal = lazy(() => import("./pages/CourseNotesJournal"));
const MasterclassNotesJournal = lazy(() => import("./pages/MasterclassNotesJournal"));
const CompanyDashboard = lazy(() => import("./pages/CompanyDashboard"));
const ResourcesHub = lazy(() => import("./pages/ResourcesHub"));
const FutureLetter = lazy(() => import("./pages/FutureLetter"));
const PrivateJournal = lazy(() => import("./pages/PrivateJournal"));
const WordsOfWisdom = lazy(() => import("./pages/WordsOfWisdom"));
const ThinkingPatternAnalyser = lazy(() => import("./pages/ThinkingPatternAnalyser"));
const CognitiveDistortionsTest = lazy(() => import("./pages/CognitiveDistortionsTest"));
const InteractiveLearningPaths = lazy(() => import("./pages/InteractiveLearningPaths"));
const LearningPathLevel = lazy(() => import("./pages/LearningPathLevel"));
const LearningLesson = lazy(() => import("./pages/LearningLesson"));
const CuratedBookLibrary = lazy(() => import("./pages/CuratedBookLibrary"));
const PublicSpeakingBooks = lazy(() => import("./pages/PublicSpeakingBooks"));
const ArticlesInsights = lazy(() => import("./pages/ArticlesInsights"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const InspiringTalks = lazy(() => import("./pages/InspiringTalks"));
const FamousSpeeches = lazy(() => import("./pages/FamousSpeeches"));
const PublicSpeakingCourses = lazy(() => import("./pages/PublicSpeakingCourses"));
const PublicSpeakingLevel = lazy(() => import("./pages/PublicSpeakingLevel"));
const PublicSpeakingLesson = lazy(() => import("./pages/PublicSpeakingLesson"));
const SupportUs = lazy(() => import("./pages/SupportUs"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Help = lazy(() => import("./pages/Help"));
const CriticalThinkingCourse = lazy(() => import("./pages/courses/CriticalThinkingCourse"));
const BiasesCourse = lazy(() => import("./pages/courses/BiasesCourse"));
const DecisionMakingCourse = lazy(() => import("./pages/courses/DecisionMakingCourse"));
const RhetoricCourse = lazy(() => import("./pages/courses/RhetoricCourse"));
const PhilosophyCourse = lazy(() => import("./pages/courses/PhilosophyCourse"));
const PublicSpeakingCourse = lazy(() => import("./pages/courses/PublicSpeakingCourse"));
const NegotiationSkillsCourse = lazy(() => import("./pages/courses/NegotiationSkillsCourse"));
const NonviolentCommunicationCourse = lazy(() => import("./pages/courses/NonviolentCommunicationCourse"));
const LeadershipStylesCourse = lazy(() => import("./pages/courses/LeadershipStylesCourse"));
const ConflictManagementCourse = lazy(() => import("./pages/courses/ConflictManagementCourse"));
const AdvocacyCourse = lazy(() => import("./pages/courses/AdvocacyCourse"));
const GroupCommunicationCourse = lazy(() => import("./pages/courses/GroupCommunicationCourse"));
const RhetoricalModesCourse = lazy(() => import("./pages/courses/RhetoricalModesCourse"));
const WorkplaceCognitionCourse = lazy(() => import("./pages/courses/WorkplaceCognitionCourse"));
const ArgumentAndDebateCourse = lazy(() => import("./pages/courses/ArgumentAndDebateCourse"));
const DeathOfPerformedPerfectionCourse = lazy(() => import("./pages/courses/DeathOfPerformedPerfectionCourse"));
const AlgorithmicBiasLegalTechCourse = lazy(() => import("./pages/courses/AlgorithmicBiasLegalTechCourse"));
const CognitiveBehaviouralRhetoricCourse = lazy(() => import("./pages/courses/CognitiveBehaviouralRhetoricCourse"));
const DialecticVsDogmaCourse = lazy(() => import("./pages/courses/DialecticVsDogmaCourse"));
const RhetoricOfAuthorityCourse = lazy(() => import("./pages/courses/RhetoricOfAuthorityCourse"));
const EnglishGrammarCourse = lazy(() => import("./pages/courses/EnglishGrammarCourse"));
const TriviumCourse = lazy(() => import("./pages/courses/TriviumCourse"));
const TriviumVerbsHub = lazy(() => import("./pages/courses/TriviumVerbsHub"));
const TriviumVerbsCourse = lazy(() => import("./pages/courses/TriviumVerbsCourse"));
const TriviumVerbsLesson = lazy(() => import("./pages/courses/TriviumVerbsLesson"));
const IntroductionToWritingCourse = lazy(() => import("./pages/courses/IntroductionToWritingCourse"));
const IntroductionToLogicCourse = lazy(() => import("./pages/courses/IntroductionToLogicCourse"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const CourseGuide = lazy(() => import("./pages/CourseGuide"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiesPolicy = lazy(() => import("./pages/CookiesPolicy"));
const ModernSlavery = lazy(() => import("./pages/ModernSlavery"));
const Workshops = lazy(() => import("./pages/Workshops"));
const WorkshopDetail = lazy(() => import("./pages/WorkshopDetail"));
const Courses = lazy(() => import("./pages/Courses"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const English = lazy(() => import("./pages/English"));
const EnglishDetail = lazy(() => import("./pages/EnglishDetail"));
const GeopoliticsBook = lazy(() => import("./pages/GeopoliticsBook"));
const BooksHub = lazy(() => import("./pages/BooksHub"));
const LibraryModule = lazy(() => import("./pages/LibraryModule"));
const GeopoliticsChapter = lazy(() => import("./pages/GeopoliticsChapter"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ExecutiveApp = lazy(() => import("./executive/ExecutiveApp"));

// Hides the Core site chrome inside the Executive Zone.
const CoreOnly = ({ children }) => {
  const { pathname } = useLocation();
  return pathname === "/executive" || pathname.startsWith("/executive/") ? null : children;
};

const PageFallback = () => (
  <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-[#F5F5DC]/30 border-t-[#F5F5DC] animate-spin" />
  </div>
);

const EnglishMastersRedirect = () => {
  const { lessonId } = useParams();
  return <Navigate to={`/learning-path/english-language/advanced/${lessonId}`} replace />;
};
const EnglishBeginnerRedirect = () => {
  const { lessonId } = useParams();
  return <Navigate to={`/learning-path/english-language/pre-intermediate/${lessonId}`} replace />;
};

function App() {
  // Register the service worker so the app stays installable as a PWA.
  // The worker is network-first (never serves stale app code) and purges old
  // caches on activate, so returning visitors always get the latest version.
  useEffect(() => {
    sessionStorage.removeItem('chunkReloaded');
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js').catch(() => {});
      });
    }
  }, []);

  return (
    <AuthProvider>
      <div className="App min-h-screen bg-[#0a0a0a]">
        <BrowserRouter>
          <RouteSeo />
          <CoreOnly><Navbar /></CoreOnly>
          {/* InstallPWAPrompt parked in reserve per request (does not affect Get the App page) */}
          {/* <InstallPWAPrompt /> */}
          <CookieConsent />
          <ChunkErrorBoundary>
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/biases" element={<BiasesHub />} />
            <Route path="/rhetoric" element={<RhetoricHub />} />
            <Route path="/public-speaking" element={<PublicSpeakingHub />} />
            <Route path="/modules" element={<ModulesHub />} />
            <Route path="/courses/critical-thinking" element={<CriticalThinkingCourse />} />
            <Route path="/courses/biases" element={<BiasesCourse />} />
            <Route path="/courses/decision-making" element={<DecisionMakingCourse />} />
            <Route path="/courses/rhetoric" element={<RhetoricCourse />} />
            <Route path="/courses/philosophy" element={<PhilosophyCourse />} />
            <Route path="/courses/public-speaking" element={<PublicSpeakingCourse />} />
            <Route path="/courses/negotiation-skills" element={<NegotiationSkillsCourse />} />
            <Route path="/courses/nonviolent-communication" element={<NonviolentCommunicationCourse />} />
            <Route path="/courses/leadership-styles" element={<LeadershipStylesCourse />} />
            <Route path="/courses/conflict-management" element={<ConflictManagementCourse />} />
            <Route path="/courses/advocacy" element={<AdvocacyCourse />} />
            <Route path="/courses/group-communication" element={<GroupCommunicationCourse />} />
            <Route path="/courses/rhetorical-modes" element={<RhetoricalModesCourse />} />
            <Route path="/courses/workplace-cognition" element={<WorkplaceCognitionCourse />} />
            <Route path="/courses/argument-and-debate" element={<ArgumentAndDebateCourse />} />
            <Route path="/courses/death-of-performed-perfection" element={<DeathOfPerformedPerfectionCourse />} />
            <Route path="/courses/dialectic-vs-dogma" element={<DialecticVsDogmaCourse />} />
            <Route path="/courses/rhetoric-of-authority" element={<RhetoricOfAuthorityCourse />} />
            <Route path="/courses/algorithmic-bias-legal-tech" element={<AlgorithmicBiasLegalTechCourse />} />
            <Route path="/courses/cognitive-behavioural-rhetoric" element={<CognitiveBehaviouralRhetoricCourse />} />
            <Route path="/courses/english-grammar" element={<EnglishGrammarCourse />} />
            <Route path="/courses/trivium" element={<TriviumCourse />} />
            <Route path="/courses/trivium-verbs" element={<TriviumVerbsHub />} />
            <Route path="/courses/trivium-verbs/:level" element={<TriviumVerbsCourse />} />
            <Route path="/courses/trivium-verbs/:level/:slug" element={<TriviumVerbsLesson />} />
            <Route path="/courses/introduction-to-writing" element={<IntroductionToWritingCourse />} />
            <Route path="/courses/introduction-to-logic" element={<IntroductionToLogicCourse />} />
            <Route path="/course-guide" element={<CourseGuide />} />
            <Route path="/cognitive-resources" element={<CognitiveResourcesHub />} />
            <Route path="/journey" element={<JourneyPage />} />
            <Route path="/corporate" element={<CorporatePage />} />
            <Route path="/corporate/english" element={<CorporateEnglish />} />
            <Route path="/corporate/english/certificate" element={<SampleCertificate />} />
            <Route path="/corporate/client/:linkId" element={<CorporateEnglish />} />
            <Route path="/corporate/aon/:linkId" element={<CorporateEnglishAon />} />
            <Route path="/my-aon" element={<AonHome />} />
            <Route path="/corporate/aon/:linkId/certificate" element={<SampleCertificate />} />
            <Route path="/corporate/client/:linkId/certificate" element={<SampleCertificate />} />
            <Route path="/corporate/:offering" element={<CorporateOffering />} />
            <Route path="/library" element={<BiasLibrary />} />
            <Route path="/bias/:id" element={<BiasDetail />} />
            <Route path="/scanner" element={<BiasScanner />} />
            <Route path="/rhetorical-scanner" element={<RhetoricalScanner />} />
            <Route path="/rhetorical-progress" element={<RhetoricalProgressTracker />} />
            <Route path="/speech-analyzer" element={<SpeechAnalyzer />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/rhetoric-devices" element={<RhetoricDevices />} />
            <Route path="/rhetoric-devices/:id" element={<RhetoricDeviceDetail />} />
            <Route path="/kinesics" element={<KinesicsLibrary />} />
            <Route path="/kinesics/:id" element={<KinesicsDetail />} />
            <Route path="/read-the-room" element={<ReadTheRoom />} />
            <Route path="/my-devices" element={<MyDevices />} />
            <Route path="/cognitive-mood-builder" element={<CognitiveMoodBuilder />} />
            <Route path="/critical-thinking-journal" element={<CriticalThinkingJournal />} />
            <Route path="/course-notes-journal" element={<CourseNotesJournal />} />
            <Route path="/masterclass-notes" element={<MasterclassNotesJournal />} />
            <Route path="/company-dashboard" element={<CompanyDashboard />} />
            <Route path="/login" element={<AuthRedirect mode="login" />} />
            <Route path="/signup" element={<AuthRedirect mode="register" />} />
            <Route path="/resources" element={<ResourcesHub />} />
            <Route path="/a-letter-to-your-future-self" element={<FutureLetter />} />
            <Route path="/trivium-grammar-for-english-professionals" element={<Navigate to="/courses/trivium" replace />} />
            <Route path="/books" element={<BooksHub />} />
            <Route path="/module/:moduleId" element={<LibraryModule />} />
            <Route path="/private-journal" element={<PrivateJournal />} />
            <Route path="/words-of-wisdom" element={<WordsOfWisdom />} />
            <Route path="/thinking-pattern-analyser" element={<ThinkingPatternAnalyser />} />
            <Route path="/thinking-pattern-test/:testId" element={<CognitiveDistortionsTest />} />
            <Route path="/interactive-learning-paths" element={<InteractiveLearningPaths />} />
            <Route path="/learning-path/english-language/beginner" element={<Navigate to="/learning-path/english-language/pre-intermediate" replace />} />
            <Route path="/learning-path/english-language/beginner/:lessonId" element={<EnglishBeginnerRedirect />} />
            <Route path="/learning-path/english-language/masters" element={<Navigate to="/learning-path/english-language/advanced" replace />} />
            <Route path="/learning-path/english-language/masters/:lessonId" element={<EnglishMastersRedirect />} />
            <Route path="/learning-path/:pathId/:levelId" element={<LearningPathLevel />} />
            <Route path="/learning-path/:pathId/:levelId/:lessonId" element={<LearningLesson />} />
            <Route path="/book-sanctuary" element={<CuratedBookLibrary />} />
            <Route path="/public-speaking-books" element={<PublicSpeakingBooks />} />
            <Route path="/articles" element={<ArticlesInsights />} />
            <Route path="/article/:articleId" element={<ArticleDetail />} />
            <Route path="/inspiring-talks" element={<InspiringTalks />} />
            <Route path="/famous-speeches" element={<FamousSpeeches />} />
            <Route path="/public-speaking-courses" element={<PublicSpeakingCourses />} />
            <Route path="/public-speaking-courses/:pathId/:levelId" element={<PublicSpeakingLevel />} />
            <Route path="/public-speaking-courses/:pathId/:levelId/lesson/:lessonId" element={<PublicSpeakingLesson />} />
            <Route path="/support-us" element={<SupportUs />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/install" element={<InstallApp />} />
            <Route path="/donation" element={<SupportUs />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogDetail />} />
            <Route path="/blogcast" element={<Blogcast />} />
            <Route path="/blogcast/:show" element={<BlogcastShow />} />
            <Route path="/workshops" element={<Workshops />} />
            <Route path="/workshops/:slug" element={<WorkshopDetail />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/course/:slug" element={<CourseDetail />} />
            <Route path="/english" element={<English />} />
            <Route path="/english/:slug" element={<EnglishDetail />} />
            <Route path="/geopolitics" element={<GeopoliticsBook />} />
            <Route path="/geopolitics/:slug" element={<GeopoliticsChapter />} />
            <Route path="/help" element={<Help />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiesPolicy />} />
            <Route path="/modern-slavery" element={<ModernSlavery />} />
            <Route path="/executive/*" element={<ExecutiveApp />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </ChunkErrorBoundary>
          <CoreOnly><Footer /></CoreOnly>
          <Toaster />
          <SonnerToaster />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;